import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AppState, useUserParams } from 'reducers';
import './AssortmentSelection.scss';
import _ from 'lodash';
import { Product, ViewId } from 'lib/types';
import { isLeaf, makeRoot, mapTree, TreeNode } from 'lib/tree';
import GutterCreator from 'components/GutterCreator';
import PlatesCreator from 'components/PlatesCreator';
import Cart from '../Cart/Cart';
import CartReset from '../Cart/CartReset';
import { getProductsByGroup, getGroups, getProductsInfo, getProductsByGroupReturnProducts } from 'lib/communication/orders';
import SimilarProducts from './ProductSelection/SimilarProducts';
import ProductSelection from './ProductSelection/ProductSelection';
import AdditionalAssortment from './ProductSelection/AdditionalAssortment';
import AssortmentSelectionHeader from './AssortmentSelectionHeader';
import BasicView from './BasicView';
import AdvancedView from './AdvancedView';
import { produce } from 'immer';
import ButtonComponent from 'components/ButtonComponent';
import useDebounce from 'lib/hooks/useDebounce';
import { useDevice } from 'lib/hooks/useDevice';
import { GA_gutterCreatorOpened, GA_productViewed } from 'lib/gtm-utils';

export const BASIC_GROUP = 'KWWW';
export const SPECIAL_GROUP = '';
export const GROUP_CATEGORY_NAME = ['Rodzaj', 'Typ', 'Powłoka', 'Grubość', 'Kolor'];
export const GROUP_ORDER = [1, 2, 3, 4, 5];

export const pathToArray = (path: string): string[] => path.split('\\');

export const isSpecialGroup = (selectedPath: string): boolean => _.includes(pathToArray(selectedPath), SPECIAL_GROUP);

export const firstNGroups = (path: string, n: number | undefined): string => pathToArray(path).slice(0, n).join('\\');

export const arrayPathToString = (pathArray: string[]): string => pathArray.join('\\');

interface AssortmentSelectionProps {
    goToSummary: () => void;
    cartReset: boolean;
    setCartReset: (value: boolean) => void;
    loadedCart: boolean;
}

export type AssortmentSelectionState = {
    selectedView: ViewId;
    selectedPath: string;
    selectedProduct?: Product | null;
    isAdditionalAssortmentExpanded: boolean,
    additionalAssortmentSelectedMainGroup: string | null,
    additionalAssortmentSelectedSubGroup: string | null,
    scrollPosition: number | null,
    editedOrderId: string | null;
};

const ASSORTMENT_SELECTION_STATE: AssortmentSelectionState = {
    selectedView: 'basic',
    selectedPath: BASIC_GROUP,
    selectedProduct: null,
    isAdditionalAssortmentExpanded: false,
    additionalAssortmentSelectedMainGroup: null,
    additionalAssortmentSelectedSubGroup: null,
    scrollPosition: null,
    editedOrderId: null
};

const AssortmentSelection = (props: AssortmentSelectionProps) => {
    const history = useHistory<AssortmentSelectionState>();
    const location = useLocation();
    const assortmentState: AssortmentSelectionState = useMemo(() => (history.location.state ? (history.location.state as AssortmentSelectionState) : ASSORTMENT_SELECTION_STATE), [history.location.state]);
    const { isDesktop } = useDevice();
    const groups = useSelector((state: AppState) => state.data.orders.groupsAndProducts.groups);
    const company = useSelector((state: AppState) => state.session.user?.company);
    const mag = useSelector((state: AppState) => state.session.user?.mag);
    const products = useSelector((state: AppState) => state.data.orders.groupsAndProducts.productsInGroup);
    const advancedTree = useSelector((state: AppState) => state.data.orders.advancedTree);
    const isFetchingGroups = useSelector((state: AppState) => state.data.orders.props.isFetchingGroups);
    const productsDetails = useSelector((state: AppState) => state.data.orders.groupsAndProducts.products);
    const isProductDetailsFetching = useSelector((state: AppState) => state.data.orders.props.isProductDetailsFetching);
    const [defaultNewOrderViewDesktop, setDefaultNewOrderViewDesktop] = useState<string>();
    const [defaultNewOrderViewMobile, setDefaultNewOrderViewMobile] = useState<string>();
    const [showCartReset, setShowCartReset] = useState(props.loadedCart && props.cartReset);
    const [loadingFromUrlParams, setLoadingFromUrlParams] = useState<boolean>(false);

    const [showGutterPromotion, setShowGutterPromotion] = useState(false);
    const [showOneTimeGutterPromotion, setShowOneTimeGutterPromotion] = useState(false);

    const lastSelectedProductSymKar = useRef(undefined as string | undefined);

    const params = useUserParams();

    useEffect(() => {
        params?.forEach((param) => {
            if (param.ParamName === 'DefaultNewOrderView') {
                setDefaultNewOrderViewDesktop(param.ParamValue);
            }
            if (param.ParamName === 'DefaultNewOrderViewMobile') {
                setDefaultNewOrderViewMobile(param.ParamValue);
            }
        });
    });

    const [showCart, setShowCart] = useState(false);
    const [selectedView, setSelectedView] = useState((isDesktop ? defaultNewOrderViewDesktop : defaultNewOrderViewMobile) as ViewId);

    useEffect(() => {
        setSelectedView((isDesktop ? defaultNewOrderViewDesktop : defaultNewOrderViewMobile) as ViewId);
    }, [isDesktop, defaultNewOrderViewDesktop, defaultNewOrderViewMobile]);

    const [rememberedSelectedView, setRememberedSelectedView] = useState<ViewId>();
    const [ifSelectedView, setIfSelectedView] = useState(false);

    useEffect(() => {
        if (!ifSelectedView && selectedView !== undefined) {
            setRememberedSelectedView(selectedView);
            setIfSelectedView(true);
        }
    }, [selectedView, defaultNewOrderViewDesktop, defaultNewOrderViewMobile]);

    useEffect(() => {
        if (selectedView === 'gutters') GA_gutterCreatorOpened();
    }, [selectedView]);

    const [selectedPath, setSelectedPath] = useState(assortmentState.selectedPath ?? BASIC_GROUP);
    const [selectedProduct, setSelectedProduct] = useState((assortmentState.selectedProduct ?? null) as Product | null);
    const [isAdditionalAssortmentExpanded, setIsAdditionalAssortmentExpanded] = useState(assortmentState.isAdditionalAssortmentExpanded ?? false);
    const [additionalAssortmentSelectedMainGroup, setAdditionalAssortmentSelectedMainGroup] = useState(assortmentState.additionalAssortmentSelectedMainGroup ?? null);
    const [additionalAssortmentSelectedSubGroup, setAdditionalAssortmentSelectedSubGroup] = useState(assortmentState.additionalAssortmentSelectedSubGroup ?? null);
    const [selectedProductPushed, setSelectedProductPushed] = useState(false);
    const [currentLevel, setCurrentLevel] = useState(0);
    const [error, setError] = useState(false);
    const [filteredTree, setFilteredTree] = useState<TreeNode[] | null>(null);
    const [findInTree, setFindInTree] = useState('');

    const inputRef = useRef<HTMLInputElement | null>(null);

    const debouncedFindInTree = useDebounce(findInTree, 300);

    const parseYYYYMMDD = (dateStr: string): Date => {
        const year = parseInt(dateStr.slice(0, 4), 10);
        const month = parseInt(dateStr.slice(4, 6), 10) - 1; // Months are 0-based
        const day = parseInt(dateStr.slice(6, 8), 10);

        return new Date(year, month, day);
    };

    useEffect(() => {
        if (props.loadedCart) {
            if (props.cartReset) {
                setShowCartReset(true);
            } else if (localStorage.getItem('gutter_one_time_promotion_showed') !== 'true' && isDesktop) {
                props.setCartReset(false);
                localStorage.setItem('gutter_one_time_promotion_showed', 'true');
                setShowOneTimeGutterPromotion(true);
            } else if (localStorage.getItem('gutter_promotion_showed') !== 'true' && selectedPath.includes('ORNI') && isDesktop) {
                props.setCartReset(false);
                if (localStorage.getItem('gutter_promotion_first_day_show') != null && localStorage.getItem('gutter_promotion_day_show') != null) {
                    const beginning = parseYYYYMMDD(localStorage.getItem('gutter_promotion_first_day_show')!);
                    const today = parseYYYYMMDD(localStorage.getItem('gutter_promotion_day_show')!);
                    if ((Math.ceil(today.getTime() - beginning.getTime()) / (1000 * 60 * 60 * 24)) > 6) return;
                }
                setShowGutterPromotion(true);
                localStorage.setItem('gutter_promotion_showed', 'true');
                localStorage.setItem('gutter_promotion_day_show', new Date().toISOString().slice(0, 10).replace(/-/g, ''));
                if (localStorage.getItem('gutter_promotion_first_day_show') == null) localStorage.setItem('gutter_promotion_first_day_show', new Date().toISOString().slice(0, 10).replace(/-/g, ''));
            } else {
                props.setCartReset(false);
            }
        }
    }, [props.loadedCart, selectedPath]);

    useEffect(() => {
        setTimeout(() => {
            if (assortmentState.scrollPosition && (lastSelectedProductSymKar.current === selectedProduct?.symKar || lastSelectedProductSymKar.current === undefined)) {
                window.scrollTo({ top: assortmentState.scrollPosition, behavior: 'smooth' });
                history.replace({
                    pathname: `${process.env.PUBLIC_URL}/new-order`,
                    state: {
                        ...history.location.state,
                        scrollPosition: null
                    }
                });
            }
        }, 200);
    }, [selectedProduct]);

    const getProductInfo = async (symKar: string, unit: string): Promise<Product | undefined> => {
        let product = _.find(productsDetails, (p) => p.symKar === symKar);
        if (product === undefined) {
            const result = await getProductsInfo([{ symKar, unit }], true);

            if (result && result.length === 1) {
                product = result[0];
            }
        }
        return product;
    };

    const selectProduct = async (symKar: string, unit: string): Promise<void> => {
        if (symKar === null || unit === null) return;

        setSelectedProductPushed(true);
        const product = await getProductInfo(symKar, unit);

        if (product) {
            GA_productViewed(product);
            history.push({
                pathname: `${process.env.PUBLIC_URL}/new-order`,
                state: {
                    ...history.location.state,
                    selectedPath: arrayPathToString(product.path),
                    selectedProduct: product,
                    isAdditionalAssortmentExpanded: false,
                    additionalAssortmentSelectedSubGroup: null,
                    additionalAssortmentSelectedMainGroup: null
                }
            });
            setSelectedProductPushed(false);
        }
    };

    const loadFromSearchParams = async () => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const symkarSearchParam = urlSearchParams.get('symkar');
        const pathSearchParam = urlSearchParams.get('path');
        const unitSearchParam = urlSearchParams.get('unit');
        if (symkarSearchParam === null && pathSearchParam === null && unitSearchParam === null) return;

        setLoadingFromUrlParams(true);
        let product: Product | undefined;
        let path: string = BASIC_GROUP;
        if (pathSearchParam !== null) {
            path = pathSearchParam;
            const productsInGroup = await getProductsByGroupReturnProducts(pathSearchParam, company);
            if (productsInGroup === false) {
                setError(true);
                return;
            }

            if (symkarSearchParam !== null) {
                const productInGroup = _.find(productsInGroup, (p) => p.symKar === symkarSearchParam);
                if (productInGroup) {
                    product = await getProductInfo(productInGroup.symKar, productInGroup.unit);
                }
            }
        } else if (symkarSearchParam !== null && unitSearchParam !== null) {
            product = await getProductInfo(symkarSearchParam, unitSearchParam);
            if (product) {
                path = arrayPathToString(product.path);
            }
        }

        history.replace({
            pathname: location.pathname,
            state: {
                ...assortmentState,
                selectedPath: path,
                selectedProduct: product
            }
        });
        setLoadingFromUrlParams(false);
    };

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const pathSearchParam = urlSearchParams.get('path');
        const symKarSearchParam = urlSearchParams.get('symkar');
        const unitSearchParam = urlSearchParams.get('unit');
        if (pathSearchParam === null && symKarSearchParam === null && unitSearchParam === null) {
            // Load history state
            const sv = ['basic', 'advanced', 'treatment', 'gutters', 'plate'].includes(history.location.state?.selectedView) ?
                history.location.state.selectedView : (rememberedSelectedView !== undefined ? rememberedSelectedView : (isDesktop ? defaultNewOrderViewDesktop : defaultNewOrderViewMobile));
            setSelectedView(sv as ViewId);

            setSelectedProduct(history.location.state?.selectedProduct ? history.location.state.selectedProduct : null);
            setSelectedPath(history.location.state?.selectedPath ? history.location.state.selectedPath : BASIC_GROUP);
            setIsAdditionalAssortmentExpanded(assortmentState.isAdditionalAssortmentExpanded ?? false);
            setAdditionalAssortmentSelectedMainGroup(assortmentState.additionalAssortmentSelectedMainGroup ?? null);
            setAdditionalAssortmentSelectedSubGroup(assortmentState.additionalAssortmentSelectedSubGroup ?? null);
        }
    }, [history.location.state, defaultNewOrderViewDesktop, defaultNewOrderViewMobile, rememberedSelectedView]);

    useEffect(() => {
        loadFromSearchParams();
    }, [location.search]);

    useEffect(() => {
        if (!groups) return;
        const group = _.find(groups, (g) => g.path === selectedPath);
        if (group) {
            setCurrentLevel(group.groups.length - 1);
        }
    }, [groups, selectedPath]);

    useEffect(() => {
        // Fetch groups
        if (!groups && !isFetchingGroups) {
            getGroups().then((result) => setError(!result));
        }
    }, [company, mag, groups]);

    const toggleTreeNode = (nodeKey: string) => {
        setFilteredTree((state) => (!state ? state :
            mapTree((node) => (node.key === nodeKey ? { ...node, expanded: !node.expanded } : node),
                makeRoot(state)).childrenNodes));
        setSelectedPath(nodeKey);
    };

    const expandAllTreeNodeParents = (nodeKey: string) => {
        const belongsToAnyNodeGroup = (node: TreeNode) => {
            if (!node.key) {
                return true;
            }
            return nodeKey.startsWith(node.key as string);
        };

        setFilteredTree((tree) => {
            if (!tree) {
                return tree;
            }
            const root = makeRoot(tree);
            const newTree = mapTree((node) => (belongsToAnyNodeGroup(node) ? { ...node, expanded: true } : { ...node, expanded: false }), root);
            return newTree.childrenNodes;
        });
        setSelectedPath(nodeKey);
    };

    const getProductsBySearch = async ({ path, key }: TreeNode, comp: string | undefined) => {
        try {
            const status = await getProductsByGroup(path, comp);
            if (!status) {
                toggleTreeNode(key);
            }
        } catch (ex) {
            alert('Błąd podczas pobierania produktu');
            toggleTreeNode(key);
        }
    };

    useEffect(() => {
        if (!advancedTree) return;

        const deepCopyTree = mapTree((node: TreeNode) => {
            const isExpanded = _.isEqual(node.path, firstNGroups(selectedPath, node.level));
            // If node is expandable and contains products fetch those products
            if (isExpanded && isLeaf(node) && node.hasProducts && node.symKar === undefined && !isFetchingGroups) {
                getProductsBySearch(node, company);
            }
            // Return node with tag if node is expanded
            return ({
                ...node,
                expanded: isExpanded
            });
        }, makeRoot(advancedTree)).childrenNodes;

        const find = findInTree;
        // Search product in tree logic
        if (find) {
            const filter: (node: TreeNode) => boolean = (node: TreeNode) => _.includes(node.description.toLowerCase(), find.toLowerCase());
            const filterTree = (treeNodes: TreeNode[]): TreeNode[] => {
                const tree: TreeNode[] = [] as TreeNode[];
                treeNodes.forEach((node) => {
                    if (filter(node)) {
                        node.expanded = false;
                        tree.push(node);
                    } else {
                        const result = filterTree(node.childrenNodes);
                        if (result.length > 0) {
                            node.childrenNodes = result;
                            node.expanded = true;
                            tree.push(node);
                        }
                    }
                });
                return tree;
            };
            const fTree = filterTree(deepCopyTree);
            setFilteredTree(fTree);
            return;
        }

        setFilteredTree(deepCopyTree);
    }, [debouncedFindInTree, advancedTree]);

    useEffect(() => {
        if (selectedView !== 'advanced') return;

        // Expand all nodes for selected product or selected path after changed product or selectedView
        const path = selectedProduct ? arrayPathToString(selectedProduct.path) : selectedPath;

        setFilteredTree((state) => (!state ? state :
            mapTree((node) => (firstNGroups(path, node.level) === node.path ? { ...node, expanded: true } : node),
                makeRoot(state)).childrenNodes));
    }, [selectedView, selectedProduct]);

    const tree = useMemo(() => {
        if (!filteredTree) return null;

        const getNodeChildrenProducts = (parentNode: TreeNode) => {
            if (parentNode.symKar) {
                return [];
            }
            const path = parentNode.path.split('\\');
            const productsFromGroup = _.filter(products, (product) => product.path.toString() === path.toString());

            return _.map(productsFromGroup, (product, index) => ({
                ...product,
                key: `${product.description}_${path}_${index}`,
                childrenNodes: []
            }));
        };

        const assignProducts = (treeNodes: TreeNode[]) => {
            treeNodes.forEach((node) => {
                if (isLeaf(node)) {
                    node.childrenNodes = getNodeChildrenProducts(node);
                } else if (node.childrenNodes.length > 0 && node.hasProducts) {
                    assignProducts(node.childrenNodes);
                    node.childrenNodes.push(...getNodeChildrenProducts(node));
                } else {
                    assignProducts(node.childrenNodes);
                }
            });
        };

        return produce(filteredTree, (draft) => assignProducts(draft));
    }, [filteredTree, products]);

    const onUpdate = async (productSymKar: string, path: string[], unit: string) => {
        selectProduct(productSymKar, unit);
    };

    const focusFirstInput = () => {
        if (!inputRef) return;
        inputRef.current?.focus();
    };

    const saveState = () => {
        lastSelectedProductSymKar.current = selectedProduct?.symKar;
        history.replace({
            pathname: `${process.env.PUBLIC_URL}/new-order`,
            state: {
                ...history.location.state,
                scrollPosition: window.scrollY,
                isAdditionalAssortmentExpanded,
                additionalAssortmentSelectedMainGroup,
                additionalAssortmentSelectedSubGroup
            }
        });
    };

    const saveStateAndGoToSumamry = () => {
        saveState();
        props.goToSummary();
    };

    return (
        <div>
            <Cart show={showCart} close={() => setShowCart(false)} goToSummary={saveStateAndGoToSumamry} />
            <CartReset show={showCartReset} close={() => { setShowCartReset(false); props.setCartReset(false); }} goToSummary={saveStateAndGoToSumamry} />
            <Card>
                <AssortmentSelectionHeader {...{ currentLevel, selectedView, setShowCart, setSelectedView, selectedPath, selectedProduct }} isSpecialGroup={isSpecialGroup(selectedPath)} />
                <Card.Body
                    className={selectedProduct || selectedView === 'advanced' ? `AssortmentSelection-${selectedView}View` : ''}
                    style={!isDesktop || ['treatment', 'gutters', 'plate'].includes(selectedView) ? { padding: '10px' } : { minHeight: '660px' }}
                >
                    {selectedView === 'basic' && <BasicView {...{ currentLevel, selectedView, selectedPath, selectedProduct, error, selectProduct, setError, setCurrentLevel, setSelectedPath, toggleTreeNode, selectedProductPushed, isLoading: loadingFromUrlParams }} />}
                    {selectedView === 'advanced' && <AdvancedView {...{ error, tree, findInTree, selectedPath, selectedProduct, selectProduct, setFindInTree, toggleTreeNode, expandAllTreeNodeParents, onUpdate, focusFirstInput, isLoading: loadingFromUrlParams }} />}
                    {
                        ['basic', 'advanced'].includes(selectedView) && !loadingFromUrlParams && selectedProduct && !isProductDetailsFetching && (
                            <>
                                <ProductSelection selectedProduct={selectedProduct} inputRef={inputRef} />
                                <SimilarProducts selectedProduct={selectedProduct} />
                                <AdditionalAssortment
                                    selectedProduct={selectedProduct}
                                    isExpanded={isAdditionalAssortmentExpanded}
                                    setIsExpanded={setIsAdditionalAssortmentExpanded}
                                    selectedMainGroup={additionalAssortmentSelectedMainGroup}
                                    setSelectedMainGroup={setAdditionalAssortmentSelectedMainGroup}
                                    selectedSubGroup={additionalAssortmentSelectedSubGroup}
                                    setSelectedSubGroup={setAdditionalAssortmentSelectedSubGroup}
                                    saveState={saveState}
                                />
                            </>
                        )
                    }
                    {
                        selectedView === 'treatment' && (
                            <div>
                                <img src={`${process.env.PUBLIC_URL}/obrobki.png`} alt='' width={1020} />
                            </div>
                        )
                    }
                    {selectedView === 'gutters' ? <GutterCreator /> : ''}
                    {selectedView === 'plate' && <PlatesCreator />}
                </Card.Body>
            </Card>
            <div className={isDesktop ? 'NewOrder-footer NewOrder-footerBegin' : 'NewOrder-footer NewOrder-footerBegin d-flex justify-content-center'}>
                <ButtonComponent
                    text='PRZEJDŹ DO PODSUMOWANIA'
                    onClick={saveStateAndGoToSumamry}
                    onHoverAnimation={3}
                />
            </div>

            <Modal show={showGutterPromotion} onHide={() => { setShowGutterPromotion(false); }} backdrop='static'>
                <Modal.Header className='NewOrder-modal-header'>
                    <Modal.Title>{`Zamawiasz rynny?`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Pamiętaj, że przygotowując zamówienie orynnowania możesz skorzystać z nowego `}<strong>KREATORA RYNIEN</strong>{`.`}</Modal.Body>
                <Modal.Footer className='NewOrder-modal-footer'>
                    <ButtonComponent margin='0' variant='dark' text='Kontynuuj' brandColoredButton={false} onClick={() => { setShowGutterPromotion(false); }} />
                    <ButtonComponent margin='0' text='Przejdź do Kreatora Rynien' variant='blue' brandColoredButton={false} onClick={() => { setShowGutterPromotion(false); setSelectedView('gutters'); }} />
                </Modal.Footer>
            </Modal>

            <Modal show={showOneTimeGutterPromotion} onHide={() => { setShowOneTimeGutterPromotion(false); }} backdrop='static'>
                <Modal.Header className='NewOrder-modal-header'>
                    <Modal.Title>{`Informacja`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{`Zachęcamy do przetestowania nowego wygodnego `}<strong>KREATORA RYNIEN</strong>{`. Wybór systemu odwadniania nigdy nie był tak prosty!`}
                </Modal.Body>
                <Modal.Footer className='NewOrder-modal-footer'>
                    <ButtonComponent margin='0' variant='dark' text='Kontynuuj' brandColoredButton={false} onClick={() => { setShowOneTimeGutterPromotion(false); }} />
                    <ButtonComponent margin='0' variant='blue' text='Przejdź do Kreatora Rynien' brandColoredButton={false} onClick={() => { setShowOneTimeGutterPromotion(false); setSelectedView('gutters'); }} />
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AssortmentSelection;
